import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import OpenInNew from "@mui/icons-material/OpenInNew";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import { useState, useEffect } from "react";

// Single source of truth for both the detail table and the copy-to-clipboard
// text below, so the two can never list a different set of fields.
const FIELD_ROWS = [
  { key: "vuosi", label: "Vuosi" },
  { key: "paikkakunta", label: "Paikkakunta" },
  { key: "kaupunginosa", label: "Kaupunginosa" },
  { key: "kylä", label: "Kylä tai kortteli" },
  { key: "numero", label: "Tilannumero tai tonttinumero" },
  { key: "tila", label: "Tila tai katu" },
  { key: "ammatti tai arvo", label: "Ammatti tai arvo" },
  { key: "etunimi", label: "Etunimi" },
  { key: "patronyymi", label: "Patronyymi" },
  { key: "sukunimi", label: "Sukunimi" },
  { key: "syntymävuosi", label: "Syntymävuosi" },
  { key: "lisätiedot", label: "Lisätiedot" },
  { key: "signum", label: "Signum" },
  { key: "sivunumero", label: "Sivunumero" },
];

function buildClipboardText(info) {
  const lines = FIELD_ROWS.map(
    ({ key, label }) => `${label}: ${info[key] ?? ""}`,
  );
  lines.push(`Linkki: ${info.linkki ?? ""}`);
  return lines.join("\n");
}

export default function BasicModal(props) {
  const [open, setOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState("");
  const info = props.row;

  const handleClose = () => {
    setOpen(false);
    props.handleClose();
    setCopySuccess(false);
  };

  useEffect(() => {
    if (props.showModal) {
      setOpen(true);
    }
  }, [setOpen, props.showModal]);

  const copyToClipBoard = () => {
    setCopySuccess("Kopioitu leikepöydälle!");
    navigator.clipboard.writeText(buildClipboardText(info));
  };

  if (!open) {
    return null;
  }

  const subtitle = [
    info.patronyymi,
    info.syntymävuosi && `s. ${info.syntymävuosi}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      <DialogTitle variant="h4" component="h2" sx={{ pr: 7 }}>
        {`${info.etunimi || ""} ${info.sukunimi || ""}`}
      </DialogTitle>
      <IconButton
        aria-label="Sulje"
        onClick={handleClose}
        sx={{ position: "absolute", right: 12, top: 14 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent dividers>
        {subtitle && (
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>
            {subtitle}
          </Typography>
        )}
        <Table size="small" aria-label="Henkilön tiedot">
          <TableBody>
            {FIELD_ROWS.filter(({ key }) => info[key]).map(({ key, label }) => (
              <TableRow
                key={key}
                sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ color: "text.secondary", width: "45%", pl: 0 }}
                >
                  {label}
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>{info[key]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        {!copySuccess && (
          <Button
            id="copyButton"
            startIcon={<ContentCopyIcon />}
            onClick={() => copyToClipBoard()}
          >
            Kopioi tiedot
          </Button>
        )}
        {copySuccess && (
          <Typography
            variant="body2"
            color="primary"
            sx={{ display: "flex", alignItems: "center", gap: 0.5, pl: 1 }}
          >
            <CheckIcon fontSize="small" />
            {copySuccess}
          </Typography>
        )}
        {info.linkki && (
          <Button
            variant="contained"
            href={info.linkki}
            target="_blank"
            endIcon={<OpenInNew />}
          >
            Avaa henkikirja
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
